// React binding for the colour scheme (src/theme/color-scheme.ts).
//
// A module-level store rather than a context provider: the preference is a single
// global with exactly one consumer shape ("what is it, set it"), it must be applied
// to <html> whether or not anything is subscribed, and keeping it out of the tree
// means the topbar control does not re-render the console when it changes — only the
// class on <html> does, which is the point.

import { useCallback, useSyncExternalStore } from 'react';
import {
  DARK_QUERY,
  applyScheme,
  readPreference,
  resolveScheme,
  writePreference,
  type ColorScheme,
  type ThemePreference,
} from './color-scheme';

let preference: ThemePreference = readPreference();
let scheme: ColorScheme = resolveScheme(preference);
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function commit(next: ThemePreference): void {
  preference = next;
  scheme = resolveScheme(next);
  applyScheme(scheme, preference);
  emit();
}

/**
 * Apply the stored preference and start following the OS.
 *
 * The pre-hydration script in index.html has already painted the right scheme, so
 * this is not what prevents the flash — it re-applies (cheap, idempotent) and, more
 * importantly, subscribes: while the preference is `system`, an operator flipping
 * their OS to dark at dusk must see the console follow without a reload. The listener
 * is deliberately never torn down — it lives as long as the document.
 */
export function startColorScheme(): void {
  applyScheme(scheme, preference);
  let media: MediaQueryList;
  try {
    media = window.matchMedia(DARK_QUERY);
  } catch {
    return; // no matchMedia: the explicit preferences still work, `system` stays light
  }
  media.addEventListener('change', () => {
    if (preference === 'system') commit('system');
  });
}

export function setThemePreference(next: ThemePreference): void {
  writePreference(next);
  commit(next);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useColorScheme(): {
  preference: ThemePreference;
  scheme: ColorScheme;
  setPreference: (next: ThemePreference) => void;
} {
  const current = useSyncExternalStore(
    subscribe,
    () => preference,
    () => 'system' as ThemePreference,
  );
  const setPreference = useCallback((next: ThemePreference) => setThemePreference(next), []);
  return { preference: current, scheme: resolveScheme(current), setPreference };
}

/** Test seam — resets the module store between cases. */
export function __resetColorSchemeForTest(): void {
  preference = readPreference();
  scheme = resolveScheme(preference);
  listeners.clear();
}
